import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  Spin,
  Table,
  Tabs,
  Tag,
} from "antd";
import {
  BellOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  HistoryOutlined,
  ThunderboltOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { post } from "../../../services/services";
import { ApiURL } from "../../../services/apiConstants";
import ViolationBadge from "../../../components/ViolationBadge";
import { VIOLATION_CONFIG } from "../../../config/violationConfig";
import VoyageDetails from "../../../components/VoyageDetails";

/* ── constants ─────────────────────────────────────────────────── */
const REGULATION_LABEL = {
  MLC_2006:          "MLC 2006",
  STCW_A_VIII_1:     "STCW VIII/1",
  MANILA_AMENDMENT:  "Manila",
  MLC_STANDARD_A1_1: "MLC A1.1",
};
const REGULATION_COLOR = {
  MLC_2006:          "blue",
  STCW_A_VIII_1:     "purple",
  MANILA_AMENDMENT:  "orange",
  MLC_STANDARD_A1_1: "cyan",
};
const BAND_CONFIG = {
  green: { color: "#52c41a", bg: "#f6ffed", border: "#b7eb8f", label: "Compliant — ≥77h rest in 7 days" },
  amber: { color: "#fa8c16", bg: "#fff7e6", border: "#ffd591", label: "Manila Exception May Apply — 70–77h" },
  red:   { color: "#f5222d", bg: "#fff2f0", border: "#ffccc7", label: "Non-Compliant — <70h rest in 7 days" },
};

/* ── sub-components ─────────────────────────────────────────────── */

const RegTag = ({ reg }) => (
  <Tag color={REGULATION_COLOR[reg] || "default"} style={{ fontSize: 11 }}>
    {REGULATION_LABEL[reg] || reg}
  </Tag>
);

const StatusTag = ({ status }) => (
  <Tag color={status === "acknowledged" ? "success" : "processing"} style={{ textTransform: "capitalize" }}>
    {status}
  </Tag>
);

const IntervalCard = ({ data, loading }) => {
  if (loading) return <div style={{ textAlign: "center", padding: "20px 0" }}><Spin /></div>;
  if (!data) return null;
  const band = BAND_CONFIG[data.band] || BAND_CONFIG.red;
  const restPct = Math.min(100, Math.round((data.rest_7d / (data.min_rest_required || 77)) * 100));
  const workPct = Math.min(100, Math.round((data.work_7d / (data.max_work_allowed || 72)) * 100));

  return (
    <Card
      style={{
        borderRadius: 14,
        border: `1.5px solid ${band.border}`,
        background: band.bg,
        marginBottom: 16,
        marginTop: 12,
      }}
      styles={{ body: { padding: "16px 20px" } }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: 24, alignItems: "center" }}>
        {/* Window + status pill */}
        <div>
          <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.5 }}>
            7-Day Rolling Window
          </div>
          <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>
            {dayjs(data.window_start).format("DD MMM")} – {dayjs(data.window_end).format("DD MMM YYYY")}
          </div>
          <div
            style={{
              display: "inline-block",
              marginTop: 8,
              background: band.color,
              color: "#fff",
              borderRadius: 20,
              padding: "3px 14px",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {band.label}
          </div>
        </div>

        {/* Progress bars */}
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
              <span style={{ color: "#52c41a" }}>
                <ClockCircleOutlined style={{ marginRight: 4 }} />
                Rest: {Number(data.rest_7d).toFixed(1)}h
              </span>
              <span style={{ color: "#888", fontSize: 11 }}>Required: {data.min_rest_required}h</span>
            </div>
            <div style={{ background: "#e8e8e8", borderRadius: 4, height: 8, overflow: "hidden" }}>
              <div style={{ width: `${restPct}%`, background: band.color, height: "100%", borderRadius: 4, transition: "width 0.5s" }} />
            </div>
          </div>
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
              <span style={{ color: "#1890ff" }}>
                <ThunderboltOutlined style={{ marginRight: 4 }} />
                Work: {Number(data.work_7d).toFixed(1)}h
              </span>
              <span style={{ color: "#888", fontSize: 11 }}>Max: {data.max_work_allowed}h</span>
            </div>
            <div style={{ background: "#e8e8e8", borderRadius: 4, height: 8, overflow: "hidden" }}>
              <div
                style={{
                  width: `${workPct}%`,
                  background: workPct >= 100 ? "#f5222d" : "#1890ff",
                  height: "100%",
                  borderRadius: 4,
                  transition: "width 0.5s",
                }}
              />
            </div>
          </div>
        </div>

        {/* Rest still needed */}
        {data.rest_still_needed > 0 && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: band.color, lineHeight: 1 }}>
              {Number(data.rest_still_needed).toFixed(1)}h
            </div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>rest still needed</div>
          </div>
        )}
        {data.work_remaining > 0 && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: "#1890ff", lineHeight: 1 }}>
              {Number(data.work_remaining).toFixed(1)}h
            </div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>work remaining</div>
          </div>
        )}
      </div>
    </Card>
  );
};

/* ── date filter row ────────────────────────────────────────────── */
const DateFilter = ({ value, onChange, onSearch, loading }) => (
  <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
    <div>
      <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Date Range</div>
      <DatePicker.RangePicker
        value={value}
        format="YYYY-MM-DD"
        disabledDate={(c) => c && c > dayjs().endOf("day")}
        onChange={onChange}
      />
    </div>
    <Button type="primary" onClick={onSearch} loading={loading}>Search</Button>
  </div>
);

/* ── shared table columns ───────────────────────────────────────── */
const violationColumns = (showStaff, extra = []) => [
  {
    title: "Date", dataIndex: "date", key: "date", width: 110, align: "center",
    sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
    render: (d) => dayjs(d).format("DD MMM YYYY"),
  },
  ...(showStaff
    ? [{ title: "Crew Member", dataIndex: "staff_name", key: "staff_name", align: "center", width: 140 }]
    : []),
  {
    title: "Violation", key: "violation", align: "left", minWidth: 260,
    render: (_, r) => <ViolationBadge rule={r.rule} detail={r.detail} />,
  },
  {
    title: "Regulation", dataIndex: "regulation", key: "regulation", align: "center", width: 120,
    render: (reg) => <RegTag reg={reg} />,
  },
  {
    title: "Status", dataIndex: "status", key: "status", align: "center", width: 120,
    render: (s) => <StatusTag status={s} />,
  },
  ...extra,
];

/* ── main component ─────────────────────────────────────────────── */
const ViolationsStaff = () => {
  const [staff_dashboard, set_staff_dashboard] = useState([]);
  const [role, setRole] = useState("");

  // 7-day interval
  const [intervalData, setIntervalData]     = useState(null);
  const [intervalLoading, setIntervalLoading] = useState(false);

  // Active violations
  const [active, setActive]             = useState([]);
  const [activeLoading, setActiveLoading] = useState(false);
  const [activeDates, setActiveDates]   = useState([dayjs().subtract(30, "day"), dayjs()]);

  // Acknowledge modal
  const [ackModal, setAckModal]     = useState({ open: false, violation: null });
  const [ackLoading, setAckLoading] = useState(false);
  const [ackForm] = Form.useForm();

  // Resolved violations
  const [resolved, setResolved]             = useState([]);
  const [resolvedLoading, setResolvedLoading] = useState(false);
  const [resolvedDates, setResolvedDates]   = useState([null, null]);
  const [resolvedPag, setResolvedPag]       = useState({ current: 1, pageSize: 20, total: 0 });

  // History
  const [history, setHistory]             = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [histPag, setHistPag]             = useState({ current: 1, pageSize: 20, total: 0 });
  const [histFilters, setHistFilters]     = useState({
    start_date: "", end_date: "", rule: "", status: "", severity: "", staff_id: "",
  });

  useEffect(() => {
    const sd = JSON.parse(localStorage.getItem("staff_dashboard") || "{}");
    set_staff_dashboard(sd);
    fetchInterval();
    fetchActive([dayjs().subtract(30, "day"), dayjs()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── fetch helpers ── */
  const fetchInterval = () => {
    setIntervalLoading(true);
    post(ApiURL.violations_interval_st, new FormData()).then((res) => {
      setIntervalData(res?.status === true ? res.data || null : null);
      setIntervalLoading(false);
    });
  };

  const fetchActive = (dates) => {
    setActiveLoading(true);
    const fd = new FormData();
    if (dates?.[0]) fd.append("start_date", dates[0].format("YYYY-MM-DD"));
    if (dates?.[1]) fd.append("end_date",   dates[1].format("YYYY-MM-DD"));
    post(ApiURL.violations_active_st, fd).then((res) => {
      setActive(res?.status === true ? res.data || [] : []);
      if (res?.role) setRole(res.role);
      setActiveLoading(false);
    });
  };

  const fetchResolved = (page = 1, dates = resolvedDates) => {
    setResolvedLoading(true);
    const fd = new FormData();
    if (dates?.[0]) fd.append("start_date", dates[0].format("YYYY-MM-DD"));
    if (dates?.[1]) fd.append("end_date",   dates[1].format("YYYY-MM-DD"));
    fd.append("page", page);
    fd.append("page_size", resolvedPag.pageSize);
    post(ApiURL.violations_resolved_st, fd).then((res) => {
      setResolved(res?.status === true ? res.data || [] : []);
      if (res?.pagination)
        setResolvedPag((p) => ({ ...p, current: res.pagination.page, total: res.pagination.total }));
      setResolvedLoading(false);
    });
  };

  const fetchHistory = (page = 1, filters = histFilters) => {
    setHistoryLoading(true);
    const fd = new FormData();
    if (filters.start_date) fd.append("start_date", filters.start_date);
    if (filters.end_date)   fd.append("end_date",   filters.end_date);
    if (filters.rule)       fd.append("rule",        filters.rule);
    if (filters.status)     fd.append("status",      filters.status);
    if (filters.severity)   fd.append("severity",    filters.severity);
    if (filters.staff_id && isCapOrCE) fd.append("staff_id", filters.staff_id);
    fd.append("page", page);
    fd.append("page_size", histPag.pageSize);
    post(ApiURL.violations_history_st, fd).then((res) => {
      setHistory(res?.status === true ? res.data || [] : []);
      if (res?.pagination)
        setHistPag((p) => ({ ...p, current: res.pagination.page, total: res.pagination.total }));
      setHistoryLoading(false);
    });
  };

  const handleAcknowledge = () => {
    ackForm.validateFields().then((vals) => {
      setAckLoading(true);
      const fd = new FormData();
      fd.append("violation_id", ackModal.violation.id);
      if (vals.corrective_action) fd.append("corrective_action", vals.corrective_action);
      if (vals.uscg_reference)    fd.append("uscg_reference",    vals.uscg_reference);
      post(ApiURL.violations_acknowledge_st, fd).then((res) => {
        setAckLoading(false);
        if (res?.status === true) {
          setAckModal({ open: false, violation: null });
          ackForm.resetFields();
          fetchActive(activeDates);
        }
      });
    });
  };

  const isCapOrCE = role === "Captain" || role === "Chief Engineer";

  const ackColumn = {
    title: "Action", key: "action", align: "center", width: 130,
    render: (_, r) =>
      role === "Captain" && r.status === "active" ? (
        <Button
          size="small"
          type="primary"
          icon={<CheckOutlined />}
          onClick={() => {
            setAckModal({ open: true, violation: r });
            ackForm.resetFields();
          }}
        >
          Acknowledge
        </Button>
      ) : null,
  };

  const ackExtraColumns = [
    {
      title: "Acknowledged At", dataIndex: "acknowledged_at", key: "ack_at", align: "center", width: 155,
      render: (v) => v ? dayjs(v).format("DD MMM YYYY HH:mm") : <span style={{ color: "#bbb" }}>—</span>,
    },
    {
      title: "Corrective Action", dataIndex: "corrective_action", key: "corrective_action", align: "left",
      render: (v) => v || <span style={{ color: "#bbb" }}>—</span>,
    },
  ];

  return (
    <>
      <VoyageDetails staff_dashboard={staff_dashboard?.voyage_details} buttonShow={false} />

      {/* 7-day interval banner */}
      <IntervalCard data={intervalData} loading={intervalLoading} />

      <Tabs
        defaultActiveKey="active"
        items={[
          /* ── Active violations ── */
          {
            key: "active",
            label: (
              <span>
                <BellOutlined />
                {" Active "}
                {active.length > 0 && (
                  <Badge count={active.length} size="small" style={{ marginLeft: 4 }} />
                )}
              </span>
            ),
            children: (
              <>
                <DateFilter
                  value={activeDates}
                  onChange={(v) => setActiveDates(v)}
                  onSearch={() => fetchActive(activeDates)}
                  loading={activeLoading}
                />
                <Table
                  bordered
                  size="small"
                  columns={violationColumns(isCapOrCE, [ackColumn])}
                  dataSource={active.map((r, i) => ({ ...r, key: r.id ?? i }))}
                  loading={activeLoading}
                  pagination={false}
                  className="custom-table"
                  locale={{ emptyText: "No active violations in this date range" }}
                  scroll={{ x: 800 }}
                />
              </>
            ),
          },

          /* ── Resolved violations ── */
          {
            key: "resolved",
            label: <span><CheckCircleOutlined /> Resolved</span>,
            children: (
              <>
                <DateFilter
                  value={resolvedDates}
                  onChange={(v) => setResolvedDates(v || [null, null])}
                  onSearch={() => fetchResolved(1)}
                  loading={resolvedLoading}
                />
                <Table
                  bordered
                  size="small"
                  columns={violationColumns(isCapOrCE, ackExtraColumns)}
                  dataSource={resolved.map((r, i) => ({ ...r, key: r.id ?? i }))}
                  loading={resolvedLoading}
                  pagination={{
                    current: resolvedPag.current,
                    pageSize: resolvedPag.pageSize,
                    total: resolvedPag.total,
                    showTotal: (t) => `${t} resolved violations`,
                    onChange: (p) => fetchResolved(p),
                  }}
                  className="custom-table"
                  locale={{ emptyText: "No resolved violations — search to load" }}
                  scroll={{ x: 900 }}
                />
              </>
            ),
          },

          /* ── History ── */
          {
            key: "history",
            label: <span><HistoryOutlined /> History</span>,
            children: (
              <>
                {/* Filter row */}
                <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
                  <div>
                    <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Date Range</div>
                    <DatePicker.RangePicker
                      format="YYYY-MM-DD"
                      disabledDate={(c) => c && c > dayjs().endOf("day")}
                      onChange={(v) =>
                        setHistFilters((p) => ({
                          ...p,
                          start_date: v?.[0]?.format("YYYY-MM-DD") || "",
                          end_date:   v?.[1]?.format("YYYY-MM-DD") || "",
                        }))
                      }
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Rule</div>
                    <Select
                      placeholder="All rules"
                      allowClear
                      style={{ width: 230 }}
                      onChange={(v) => setHistFilters((p) => ({ ...p, rule: v || "" }))}
                      options={Object.entries(VIOLATION_CONFIG).map(([r, c]) => ({ label: c.label, value: r }))}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Status</div>
                    <Select
                      placeholder="All"
                      allowClear
                      style={{ width: 140 }}
                      onChange={(v) => setHistFilters((p) => ({ ...p, status: v || "" }))}
                      options={[
                        { label: "Active", value: "active" },
                        { label: "Acknowledged", value: "acknowledged" },
                      ]}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Severity</div>
                    <Select
                      placeholder="All"
                      allowClear
                      style={{ width: 120 }}
                      onChange={(v) => setHistFilters((p) => ({ ...p, severity: v || "" }))}
                      options={[
                        { label: "Violation", value: "violation" },
                        { label: "Warning", value: "warning" },
                      ]}
                    />
                  </div>
                  {isCapOrCE && (
                    <div>
                      <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Staff ID</div>
                      <Input
                        placeholder="Staff ID"
                        style={{ width: 100 }}
                        onChange={(e) => setHistFilters((p) => ({ ...p, staff_id: e.target.value }))}
                      />
                    </div>
                  )}
                  <Button type="primary" onClick={() => fetchHistory(1)} loading={historyLoading}>
                    Search
                  </Button>
                </div>

                <Table
                  bordered
                  size="small"
                  columns={violationColumns(isCapOrCE, ackExtraColumns)}
                  dataSource={history.map((r, i) => ({ ...r, key: r.id ?? i }))}
                  loading={historyLoading}
                  pagination={{
                    current: histPag.current,
                    pageSize: histPag.pageSize,
                    total: histPag.total,
                    showTotal: (t) => `${t} records`,
                    onChange: (p) => fetchHistory(p),
                  }}
                  className="custom-table"
                  locale={{ emptyText: "Apply filters and click Search to load history" }}
                  scroll={{ x: 900 }}
                />
              </>
            ),
          },
        ]}
      />

      {/* Acknowledge modal — Captain only */}
      <Modal
        open={ackModal.open}
        onCancel={() => setAckModal({ open: false, violation: null })}
        onOk={handleAcknowledge}
        okText="Acknowledge"
        confirmLoading={ackLoading}
        title={
          <span>
            <WarningOutlined style={{ color: "#fa8c16", marginRight: 8 }} />
            Acknowledge Violation
          </span>
        }
        width={500}
      >
        {ackModal.violation && (
          <div style={{ marginBottom: 16 }}>
            <ViolationBadge rule={ackModal.violation.rule} detail={ackModal.violation.detail} />
            <div style={{ fontSize: 12, color: "#888", marginTop: 6 }}>
              {dayjs(ackModal.violation.date).format("DD MMM YYYY")}
              {ackModal.violation.staff_name && ` · ${ackModal.violation.staff_name}`}
              {ackModal.violation.regulation && (
                <RegTag reg={ackModal.violation.regulation} />
              )}
            </div>
          </div>
        )}
        <Form form={ackForm} layout="vertical">
          <Form.Item name="corrective_action" label="Corrective Action">
            <Input.TextArea rows={3} placeholder="Describe the corrective action taken…" />
          </Form.Item>
          <Form.Item name="uscg_reference" label="USCG Reference (optional)">
            <Input placeholder="e.g. USCG-2026-XXXXX" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ViolationsStaff;
